import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

type DatePickerProps = {
  label: string;
  value: string;
  min?: string;
  max?: string;
  rangeStart?: string;
  rangeEnd?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

const WEEKDAYS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
const MONTH_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

export function DatePicker({
  label,
  value,
  min,
  max,
  rangeStart,
  rangeEnd,
  disabled = false,
  onChange,
}: DatePickerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(parseDate(value) ?? new Date()));

  useEffect(() => {
    const selectedDate = parseDate(value);
    if (selectedDate) setVisibleMonth(startOfMonth(selectedDate));
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const previousMonth = addMonths(visibleMonth, -1);
  const nextMonth = addMonths(visibleMonth, 1);
  const days = buildCalendarDays(visibleMonth);
  const today = toDateValue(new Date());

  const handleSelect = (dateValue: string) => {
    if (isOutsideBounds(dateValue, min, max)) return;
    onChange(dateValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="dd-date-picker" ref={rootRef}>
      <span className="dd-date-picker-label">{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className={`dd-date-picker-trigger${isOpen ? " is-open" : ""}`}
        aria-label={`${label} ${formatDate(value)}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>{formatDate(value)}</span>
        <CalendarDays aria-hidden="true" />
      </button>

      {isOpen ? (
        <div className="dd-calendar-popover" role="dialog" aria-label={`Choisir la date ${label.toLowerCase()}`}>
          <div className="dd-calendar-header">
            <button
              type="button"
              aria-label="Mois précédent"
              disabled={!monthIntersectsBounds(previousMonth, min, max)}
              onClick={() => setVisibleMonth(previousMonth)}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <strong>{MONTH_FORMATTER.format(visibleMonth)}</strong>
            <button
              type="button"
              aria-label="Mois suivant"
              disabled={!monthIntersectsBounds(nextMonth, min, max)}
              onClick={() => setVisibleMonth(nextMonth)}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="dd-calendar-weekdays" aria-hidden="true">
            {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
          </div>

          <div className="dd-calendar-grid" role="grid">
            {days.map((date) => {
              const dateValue = toDateValue(date);
              const outsideMonth = date.getUTCMonth() !== visibleMonth.getUTCMonth();
              const unavailable = isOutsideBounds(dateValue, min, max);
              const selected = dateValue === value;
              const inRange = isDateInRange(dateValue, rangeStart, rangeEnd);
              const classNames = ["dd-calendar-day"];
              if (outsideMonth) classNames.push("is-outside-month");
              if (unavailable) classNames.push("is-unavailable");
              if (inRange) classNames.push("is-in-range");
              if (selected) classNames.push("is-selected");
              if (dateValue === today) classNames.push("is-today");

              return (
                <button
                  key={dateValue}
                  type="button"
                  role="gridcell"
                  className={classNames.join(" ")}
                  aria-label={DATE_FORMATTER.format(date)}
                  aria-selected={selected}
                  disabled={unavailable}
                  onClick={() => handleSelect(dateValue)}
                >
                  {date.getUTCDate()}
                </button>
              );
            })}
          </div>

          <div className="dd-calendar-footer">
            <span>{formatDate(rangeStart)} → {formatDate(rangeEnd)}</span>
            <button
              type="button"
              disabled={isOutsideBounds(today, min, max)}
              onClick={() => handleSelect(today)}
            >
              Aujourd’hui
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseDate(value: string | undefined): Date | null {
  if (!value) return null;
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp);
}

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, amount: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1));
}

function buildCalendarDays(month: Date): Date[] {
  const firstWeekday = (month.getUTCDay() + 6) % 7;
  const gridStart = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1 - firstWeekday));
  return Array.from({ length: 42 }, (_, index) => (
    new Date(Date.UTC(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + index))
  ));
}

function toDateValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDate(value: string | undefined): string {
  const date = parseDate(value);
  if (!date) return "—";
  return DATE_FORMATTER.format(date);
}

function isOutsideBounds(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return true;
  if (max && value > max) return true;
  return false;
}

function isDateInRange(value: string, start?: string, end?: string): boolean {
  if (!start || !end) return false;
  return value >= start && value <= end;
}

function monthIntersectsBounds(month: Date, min?: string, max?: string): boolean {
  const monthStart = toDateValue(month);
  const monthEnd = toDateValue(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)));
  if (min && monthEnd < min) return false;
  if (max && monthStart > max) return false;
  return true;
}
